import { PagedResponse } from './Paging';
import { StorageType } from './StorageType.enum';
import { UnifiedBillingAccount } from 'mbs-ui-shared@models/storage-accounts/UnifiedBillingAccount';

export enum ImmutabilityMode {
  Disabled = 0,
  Compliance = 1,
  Governance = 2
}

export class StorageBucket {
  public BucketName: string;
  public DestinationName: string;
  public Id: string;
  public ImmutabilityMode: ImmutabilityMode;
  public Region: string;
  public RegionDisplayName: string;
}

export class StorageBucketCamelCase {
  public bucketName: string;
  public destinationName: string;
  public id: string;
  public immutabilityMode: ImmutabilityMode;
  public region: string;
  public regionDisplayName: string;
}

export enum StorageAccountState {
  FullyAccessible = 'FullyAccessible',
  ReadOnly = 'ReadOnly',
  Disabled = 'Disabled',
  Deleted = 'Deleted'
}

export class StorageAccountLegacy {
  public AzureType: number;
  public Buckets?: StorageBucket[];
  public Id: string;
  public IsNonArchiveSyntheticSupported: boolean;
  public IsSyntheticSupported: boolean;
  public MinimumStorageDuration: number;
  public Name: string;
  public S3CompatibleSynthetic: boolean;
  public StorageAccountState?: StorageAccountState;
  public StorageType: StorageType;
  public disabled?: boolean; // internal property
}

export type StorageAccountV1 = {
  azureType: number;
  storageType: StorageType;
  buckets?: StorageBucketCamelCase[];
  id: string;
  isNonArchiveSyntheticSupported: boolean;
  isSyntheticSupported: boolean;
  minimumStorageDuration: number;
  name: string;
  s3CompatibleSynthetic: boolean;
  storageAccountState?: StorageAccountState;
  disabled?: boolean; // internal property
};

export type StorageAccountV2 = {
  id: string;
  displayName: string;
  azureType: number;
  cloudStorageId: string;
  destinationCount: number;
  storageType: StorageType;
  usersCount: number;
  storageUsage: number;
  isPrivateStorage: boolean;
  supportedBucketLocation: boolean;
  supportedStorageClass: boolean;
  supportedS3Accelerator: boolean;
  supportedExternalBucket: boolean;
  supportedLifecycleRules: boolean;
  supportedActions: boolean;
  isRemovableDataStorage: boolean;
  immutabilitySupported: boolean;
  isDeleting: boolean;
  dateCreated: string;
  minimumStorageDuration: number;
  accountS3LifecycleRule: S3LifecycleRules;
  accountAzureLifecycleRule: AzureLifecycleRules;
  unifiedBillingAccount: UnifiedBillingAccount;
}

export type StorageAccountV2Short = {
  id: string;
  displayName: string;
  cloudStorageId: string;
  destinationCount: number;
  usersCount: number;
  storageUsage: number;
  isAzureCredentialsEmpty: boolean;
  storageType: StorageType;
  isPrivateStorage: boolean;
  isRemovableDataStorage: boolean;
  isDeleting: boolean;
  dateCreated: string;
  minimumStorageDuration: string;
  countOfBucketsWithCustomPolicy: number;
  accountS3LifecycleRule: S3LifecycleRules;
  accountAzureLifecycleRule: AzureLifecycleRules;
  unifiedBillingAccount: UnifiedBillingAccount;
  appsUsersCount: number;
  isResellerAccount: boolean;
  isDeletionCanBeCanceled: boolean;
  isAmazonGovStorage: boolean;
  isLifecycleRulesSupported: boolean;
}

export type StorageAccountRegion = {
  displayName: string;
  systemName: string;
};

export type BucketInfo = {
  bucketName: string;
  cloudBucketID?: string;
  immutabilitySupported?: boolean;
  region?: StorageAccountRegion;
  s3AcceleratorEnabled?: boolean;
  warnings?: string[];
};

export type SetDestinationType = {
  createNewBucket?: boolean;
  displayBucketName: string;
  immutabilityEnabled?: boolean;
  googleStorageClass?: number;
  assignToCompanies?: string[];
  bucket: BucketInfo;
  isExternalBucket?: boolean;
  credential?: { code: string; redirectUri: string };
};

export type SetStorageType = {
  destination: SetDestinationType;
  options?: {
    restrictBuckets: boolean,
    allowedBuckets: string
  }
}

export type S3LifecycleRules = {
  usingS3ToStandardIADays: number | null;
  usingS3ToOneZoneIADays: number | null;
  usingS3ToGlacierDays: number | null;
  usingS3ToGlacierInstantRetrievalDays: number | null;
  usingS3ToGlacierDeepArchiveDays: number | null;
  usingS3ToIntelligentTierDays: number | null;
  usingS3CleanupUploadsDays: number | null;
};

export type AzureLifecycleRules = {
  toCool: number;
  toArchive: number;
};

export type LifecycleRulesRequest<Data, Credential> = {
  lifecyclePolicy: Data;
  credential?: Credential;
  overwriteAll?: boolean;
};

export type S3LifecycleRulesRequest = LifecycleRulesRequest<S3LifecycleRules, undefined>;
export type AzureLifecycleRulesRequest = LifecycleRulesRequest<
  AzureLifecycleRules,
  {
    code: string;
    redirectUri: string;
  }
>;

export type Destination = {
  id: string;
  accountId: string;
  displayStorageName: string;
  bucket: BucketInfo;
  displayName: string;
  storageType: StorageType;
  users: number;
  appsUsers: number;
  mailUsing: boolean;
  immutabilityEnabled: boolean;
  s3LifecycleRule: S3LifecycleRules & { isAccountPolicy: boolean };
  azureLifecycleRule: AzureLifecycleRules & { isAccountPolicy: boolean };
  isRemovable: boolean;
  assignedCompanies: string[];
  isExternalBucket: boolean;
  isReadOnly: boolean;
};

export type GoogleStorageClass = {
  id: GoogleStorageClassEnum;
  name: string;
};

export type DestinationsResponse = PagedResponse<Destination>;

export type StoragesAvailableForCreation = { type: StorageType; disabled: boolean }[];

export interface ValidateResponse {
  accountId: string;
  error: string;
}

export interface ValidateRequestBody {
  type: StorageType;
  displayName: string;
  amazonS3?: {
    arnRole: string;
    accessKey: string;
    secretKey: string;
    restrictBuckets: boolean;
    isGov: boolean;
  };
  b2?: {
    accountId: string;
    applicationKey: string;
  };
  fileSystem?: {
    path: string;
  };
  ftp?: {
    user: string;
    password: string;
    address: string;
    doNotCheckCredentials: boolean;
    useSSL: boolean;
    keepAlive: boolean;
    usePassiveMode: boolean;
  };
  sftp?: {
    user: string;
    password: string;
    address: string;
    doNotCheckCredentials: boolean;
    keepAlive: boolean;
  };
  googleCloud?: {
    serviceAccountEmail: string;
    projectId: string;
    binaryKeyInBase64: string;
  };
  azure?: {
    accountType: string;
    endpoint: string;
    accountName: string;
    sharedKey: string;
  };
  minio?: {
    accessKey: string;
    secretKey: string;
    httpEndpoint: string;
    httpsEndpoint: string;
    ignoreCertificate: boolean;
    privateStorage: boolean;
  };
  s3Compatible?: {
    accessKey: string;
    secretKey: string;
    httpEndpoint: string;
    httpsEndpoint: string;
    ignoreCertificate: boolean;
    privateStorage: boolean;
    signatureVersion: number;
    useNativeMultipartUpload: boolean;
    syntheticBackup: boolean;
  };
  wasabi?: {
    accessKey: string;
    secretKey: string;
  };
  scality?: {
    accessKey: string;
    secretKey: string;
    httpEndpoint: string;
    httpsEndpoint: string;
    privateStorage: boolean;
    signatureVersion: number;
    useNativeMultipartUpload: boolean;
    syntheticBackup: boolean;
  };
  azureVm?: {
    subscriptionId: string;
    refreshToken: string;
    tenantId: string;
  };
}

export enum GoogleStorageClassEnum {
  MultiRegional = 5
}
